import React, { useCallback } from 'react';
import { AtomWrapper } from '../AtomWrapper/AtomWrapper';
import { PermissionHelper } from '../common/PermissionHelper';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { TypeaheadOption } from './parts/types';
import { SelectTypeaheadBase, BaseProps } from './parts/SelectTypeaheadBase';
import { RecentItemsProvider } from './parts/RecentItemsProvider';
import { FieldValue } from '../../types';

export interface SelectTypeaheadAtomProps<OptionValue, TargetModel extends any = OptionValue>
  extends Omit<
    BaseProps<OptionValue, TargetModel>,
    'getValue' | 'multiple' | 'readonlyValueLabel' | 'selectedValueLabel'
  > {
  mapOutputValue?: (value?: OptionValue) => TargetModel | undefined | null;
  value?: TargetModel | null;
  /** Gets the read value label of the select */
  mapReadOnlyValue?: (value?: TargetModel) => (TargetModel extends any[] ? string[] : string) | undefined;
  recentItems?: TypeaheadOption<FieldValue>[];
}

export function SelectTypeaheadAtom<OptionValue extends any = string, TargetModel extends any = OptionValue>({
  mapOutputValue,
  mapReadOnlyValue,
  value,
  disablePermissionDebugger,
  recentItems,
  ...props
}: SelectTypeaheadAtomProps<OptionValue, TargetModel>) {
  const { getFieldPermissions, permissionDebugger, readonly } = useSmartFormContext();
  const permissions = getFieldPermissions(props.name);
  const getValue = useCallback(
    (options?: TypeaheadOption<OptionValue>[]) => {
      const option = options?.[0];
      return {
        targetModel: mapOutputValue ? (mapOutputValue(option?.value) as TargetModel) : (option?.value as TargetModel),
        optionValue: option ? [option?.value] : []
      };
    },
    [mapOutputValue]
  );

  const selectedOption: TypeaheadOption<OptionValue> | undefined = props.selected?.[0] ?? undefined;

  const selectedValueLabel = selectedOption?.label
    ? `${selectedOption.label}${selectedOption.code ? ` (${selectedOption.code})` : ''}`
    : undefined;
  const readonlyValueLabel = selectedValueLabel ?? (mapReadOnlyValue && !!value ? mapReadOnlyValue(value) : undefined);

  return (
    <RecentItemsProvider
      name={props.name}
      recentItems={recentItems}
      values={props.values}
      isApiDriven={!!props.onFilterChange}
      loading={props.loading}
    >
      <AtomWrapper name={props.name}>
        <SelectTypeaheadBase<OptionValue, TargetModel>
          getValue={getValue}
          multiple={false}
          selectedValueLabel={selectedValueLabel}
          readonlyValueLabel={readonlyValueLabel as string}
          {...props}
        />
        {!readonly && permissionDebugger && !disablePermissionDebugger && <PermissionHelper permission={permissions} />}
      </AtomWrapper>
    </RecentItemsProvider>
  );
}
